// src/pages/integrations/CredentialDetail.jsx — SOUND-709
//
// The credential record. Three things live here that could not exist before:
//
//   1. A REAL PASSWORD FIELD. 27 of 42 prod credentials are portal logins with
//      no secret storage at all, so passwords went into the free-text notes box
//      — 7 in July, 17 by late August. This is the field that was missing.
//   2. THE CONSUMER LIST. Gateway 1310948 serves 6 MIDs across 5 processors on
//      ONE credential. Deactivating it without showing that takes down five MIDs
//      nobody was looking at.
//   3. REVEAL, audit-logged server-side on every call.
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import PageHeader from "components/PageLayout";
import RecordStatusPill from "components/Records/RecordStatusPill";
import RelativeTime from "components/RelativeTime";
import ConfirmDialog from "components/ConfirmDialog";
import { useToast } from "components/Toast";
import { CREDENTIAL_KIND, statusToneFrom } from "utilities/statusTones";
import { hasSecretStorage, looksLikeSecret } from "utilities/credentialFields";
import { summarizeConsumers, revokeImpact } from "utilities/credentialConsumers";
import { ArrowLeft, Eye, Save, Trash2, Link2, Pencil } from "lucide-react";
import CredentialAttachModal from "./CredentialAttachModal";

const kindTone = statusToneFrom(CREDENTIAL_KIND);

export default function CredentialDetail() {
  const { credentialId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = isInternal(user);

  const [cred, setCred] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(null);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  // SOUND-709 — the rail was read-only, so a typo'd label or a portal URL that
  // moved could only be fixed from the MID page's legacy edit modal, and not at
  // all for a credential with no MID. Editing lives where the value is read.
  const [editing, setEditing] = useState(null); // field key
  const [draft, setDraft] = useState("");
  const [savingField, setSavingField] = useState(false);

  async function saveField(key, value) {
    setSavingField(true);
    try {
      // Only what PUT /credentials/:id actually accepts. `provider` is
      // deliberately NOT editable inline: changing it changes which secret
      // fields the credential is supposed to hold, which is a migration, not a
      // typo fix.
      const body = key.startsWith("portal.")
        ? { portal: { ...(cred.portal || {}), [key.slice(7)]: value } }
        : { [key]: value };
      await makeApiRequest("put", apiRoutes.updateCredential(credentialId), auth, user?.companyId, body);
      setEditing(null);
      await load();
      toast({ title: "Saved" });
    } catch (err) {
      toast({ status: "destructive", title: "Could not save", description: err.response?.data?.error || err.message });
    } finally {
      setSavingField(false);
    }
  }

  // Memoised on the primitives: a fresh object each render would make every
  // callback that lists it re-fire in a loop, and omitting it goes stale the
  // moment the access token refreshes.
  const auth = useMemo(
    () => ({ id: user?.id, accessToken: user?.accessToken }),
    [user?.id, user?.accessToken]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await makeApiRequest("get", apiRoutes.getCredential(credentialId), auth, user?.companyId);
      setCred(res.data?.data ?? res.data ?? null);
    } catch {
      setCred(null);
    } finally {
      setLoading(false);
    }
  }, [credentialId, auth, user?.companyId]);

  useEffect(() => { load(); }, [load]);

  async function onReveal() {
    try {
      const res = await makeApiRequest("post", apiRoutes.revealCredential(credentialId), auth, user?.companyId, {});
      setRevealed(res.data?.data?.values ?? {});
      // Said plainly, because it is true and the user should know it before
      // they click, not after.
      toast({ title: "Revealed", description: "This access has been recorded in the change log." });
    } catch (err) {
      toast({ title: "Could not reveal", description: err.response?.data?.error || err.message, variant: "destructive" });
    }
  }

  async function onSavePassword() {
    if (!password) return;
    setSaving(true);
    try {
      await makeApiRequest("put", apiRoutes.setCredentialSecrets(credentialId), auth, user?.companyId, {
        secrets: { password },
      });
      setPassword("");
      setRevealed(null);
      await load();
      toast({ title: "Password saved", description: "Stored in the credential vault, not in notes." });
    } catch (err) {
      toast({ title: "Could not save", description: err.response?.data?.error || err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!cred) return <div className="p-8 text-sm text-muted-foreground">Credential not found.</div>;

  const consumers = summarizeConsumers({
    midLinks: cred._consumers?.midLinks || [],
    integrations: cred._consumers?.integrations || [],
    pipelineConfigs: cred._consumers?.pipelineConfigs || [],
  });
  const impact = revokeImpact(consumers);
  const noStorage = !hasSecretStorage(cred);
  const notesLookSecret = looksLikeSecret(cred.portal?.notes);

  return (
    <>
      <PageHeader
        title={cred.label || "Credential"}
        subtitle={`${cred.provider}${cred.companyName ? ` · ${cred.companyName}` : ""}`}
        actions={
          <button className="btn btn-ghost" onClick={() => navigate("/credentials")}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        }
      />
      <div className="container grid gap-6 py-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          {/* ── the gap this page exists to close ── */}
          {noStorage && (
            <div className="rounded-lg border border-warning bg-warning/10 p-4">
              <p className="text-sm font-medium">No secret storage</p>
              <p className="mt-1 text-sm text-muted-foreground">
                This credential has nowhere to keep a password. Set one below — it goes to the
                credential vault, never to Mongo and never to notes.
              </p>
            </div>
          )}
          {notesLookSecret && (
            <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
              <p className="text-sm font-medium">The notes field looks like it contains a secret</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Move it into the password field below, then clear the note. Notes are visible
                wherever this credential is, and are not audit-logged when read.
              </p>
            </div>
          )}

          <section className="rounded-lg border border-border bg-card p-4">
            <h2 className="text-sm font-semibold">Secret</h2>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <label className="flex-1">
                <span className="mb-1 block text-xs text-muted-foreground">Password</span>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={hasSecretStorage(cred) ? "Replace stored password" : "Set a password"}
                  className="input w-full"
                />
              </label>
              <button className="btn btn-primary" disabled={!password || saving} onClick={onSavePassword}>
                <Save className="mr-1 h-4 w-4" /> {saving ? "Saving…" : "Save"}
              </button>
              {hasSecretStorage(cred) && (
                <button className="btn btn-secondary" onClick={onReveal}>
                  <Eye className="mr-1 h-4 w-4" /> Reveal
                </button>
              )}
            </div>
            {revealed && (
              <dl className="mt-4 space-y-1 rounded border border-border bg-muted/40 p-3 text-sm">
                {Object.entries(revealed).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="w-28 shrink-0 text-muted-foreground">{k}</dt>
                    <dd className="break-all font-mono">{v}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>

          {/* ── who is using it — must exist BEFORE revoke does ── */}
          <section className="rounded-lg border border-border bg-card p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold">
                Used by {consumers.total > 0 && <span className="text-muted-foreground">({consumers.total})</span>}
              </h2>
              {isAdmin && (
                <button className="btn btn-secondary btn-sm" onClick={() => setShowAttach(true)}>
                  <Link2 className="mr-1 h-4 w-4" /> Use on another MID
                </button>
              )}
            </div>
            {consumers.total === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground">Nothing is using this credential.</p>
            ) : (
              <ul className="mt-2 space-y-1 text-sm">
                {consumers.rows.map((r) => (
                  <li key={`${r.type}-${r.id}`} className="flex items-start justify-between gap-3 border-b border-border py-2 last:border-0">
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{r.label}</span>
                      {r.detail && (
                        <span className="block truncate text-xs text-muted-foreground">{r.detail}</span>
                      )}
                    </span>
                    <span className="shrink-0 text-right text-xs text-muted-foreground">
                      <span className="block">{r.type}</span>
                      {/* Gateway last and dimmest: it explains why these MIDs
                          are grouped, but it is not what identifies them. */}
                      {r.gateway && <span className="block opacity-60">gw {r.gateway}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-4 text-sm">
            {/* Label first: it is the thing a person actually renames, and the
                only identity the credential has that a human chose. */}
            {editing === "label" ? (
              <EditRow label="Label" value={cred.label} saving={savingField}
                onSave={(v) => saveField("label", v)} onCancel={() => setEditing(null)} />
            ) : (
              <Row label="Label" onEdit={isAdmin ? () => setEditing("label") : undefined}>{cred.label || "—"}</Row>
            )}

            <Row label="Type"><RecordStatusPill status={cred.credentialType} toneFor={kindTone} /></Row>
            {/* Provider is NOT editable inline. Changing it changes which secret
                fields the credential is supposed to hold — a migration, not a
                typo fix. */}
            <Row label="Provider"><span className="capitalize">{cred.provider}</span></Row>
            {isAdmin && <Row label="Owner">{cred.companyName || cred.companySlug || "—"}</Row>}

            {editing === "portal.username" ? (
              <EditRow label="Username" value={cred.portal?.username || cred.account?.username} saving={savingField}
                onSave={(v) => saveField("portal.username", v)} onCancel={() => setEditing(null)} />
            ) : (
              <Row label="Username" onEdit={isAdmin ? () => setEditing("portal.username") : undefined}>
                {cred.account?.username || cred.portal?.username || "—"}
              </Row>
            )}

            {editing === "portal.url" ? (
              <EditRow label="Portal URL" value={cred.portal?.url} saving={savingField}
                onSave={(v) => saveField("portal.url", v)} onCancel={() => setEditing(null)} />
            ) : (
              <Row label="URL" onEdit={isAdmin ? () => setEditing("portal.url") : undefined}>
                {cred.portal?.url ? (
                  <a className="text-brand underline" href={cred.portal.url} target="_blank" rel="noreferrer">Open portal</a>
                ) : "—"}
              </Row>
            )}

            {editing === "portal.has2fa" ? (
              <EditRow label="2FA" type="checkbox" value={cred.portal?.has2fa} saving={savingField}
                onSave={(v) => saveField("portal.has2fa", v)} onCancel={() => setEditing(null)} />
            ) : (
              <Row label="2FA" onEdit={isAdmin ? () => setEditing("portal.has2fa") : undefined}>
                {cred.portal?.has2fa ? "Yes — you will also need a code" : "No"}
              </Row>
            )}

            {editing === "visibility" ? (
              <EditRow label="Visibility" value={cred.visibility} saving={savingField}
                onSave={(v) => saveField("visibility", v)} onCancel={() => setEditing(null)} />
            ) : (
              <Row label="Visibility" onEdit={isAdmin ? () => setEditing("visibility") : undefined}>
                {cred.visibility || "internal"}
              </Row>
            )}

            <Row label="Last used">
              {cred.lastAccess?.at ? <><RelativeTime value={cred.lastAccess.at} /> · {cred.lastAccess.consumer}</> : "Never"}
            </Row>
            <Row label="Storage">{hasSecretStorage(cred) ? Object.keys(cred.vault.fields).join(", ") : "None"}</Row>
          </div>

          {isAdmin && (
            <button className="btn btn-destructive w-full" onClick={() => setConfirmRevoke(true)}>
              <Trash2 className="mr-1 h-4 w-4" /> Deactivate
            </button>
          )}
        </aside>
      </div>

      {showAttach && (
        <CredentialAttachModal
          credential={cred}
          onClose={() => setShowAttach(false)}
          onAttached={load}
        />
      )}

      <ConfirmDialog
        open={confirmRevoke}
        onCancel={() => setConfirmRevoke(false)}
        title="Deactivate this credential?"
        // The impact message is computed from the consumer list, not written by
        // hand — severity follows COUNT, because the dangerous case is the one
        // that looks routine: a single portal login quietly serving six MIDs.
        description={impact.message}
        confirmLabel="Deactivate"
        variant={impact.severity === "high" ? "destructive" : "default"}
        onConfirm={async () => {
          try {
            await makeApiRequest("delete", apiRoutes.deleteCredential(credentialId), auth, user?.companyId);
            toast({ title: "Deactivated" });
            setConfirmRevoke(false);
            navigate("/credentials");
          } catch (err) {
            toast({ title: "Could not deactivate", description: err.response?.data?.error || err.message, variant: "destructive" });
            setConfirmRevoke(false);
          }
        }}
      />
    </>
  );
}

function Row({ label, children, onEdit }) {
  return (
    <div className="group flex items-start justify-between gap-3 border-b border-border py-2 last:border-0">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className="flex min-w-0 items-center gap-1 text-right">
        <span className="min-w-0 truncate">{children}</span>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            title={`Edit ${label.toLowerCase()}`}
            // Visible on hover rather than always: seven pencils in a column
            // reads as a form, and this rail is mostly for reading.
            className="btn btn-ghost h-5 w-5 shrink-0 p-0 opacity-0 transition group-hover:opacity-60 hover:!opacity-100"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </span>
    </div>
  );
}

/** An inline editor for one rail field. Enter saves, Escape cancels. */
function EditRow({ label, value, type = "text", onSave, onCancel, saving }) {
  const [v, setV] = useState(value ?? "");
  return (
    <div className="border-b border-border py-2 last:border-0">
      <span className="mb-1 block text-xs text-muted-foreground">{label}</span>
      {type === "checkbox" ? (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!v} onChange={(e) => setV(e.target.checked)} />
          {v ? "Yes" : "No"}
        </label>
      ) : (
        <input
          autoFocus
          className="input w-full text-sm"
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSave(v);
            if (e.key === "Escape") onCancel();
          }}
        />
      )}
      <div className="mt-2 flex justify-end gap-2">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={() => onSave(v)}>
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
